"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.SharePointTransformUtils = void 0;
exports.createBaseTrialFromSharePoint = createBaseTrialFromSharePoint;
exports.transformSharePointItems = transformSharePointItems;
exports.validateClinicalTrial = validateClinicalTrial;
const index_1 = require("../index");
const sharepoint_mapping_json_1 = __importDefault(require("./sharepoint-mapping.json"));
/**
 * Transform utilities for SharePoint data
 */
class SharePointTransformUtils {
    static parseDate(dateString) {
        if (!dateString)
            return undefined;
        const date = new Date(dateString);
        return isNaN(date.getTime()) ? undefined : date;
    }
    static cleanPhoneNumber(phone) {
        if (!phone)
            return "";
        // Remove common phone number formatting
        return phone.replace(/[^\d\-\+\(\)\s]/g, "").trim();
    }
    static validateNctId(nctId) {
        if (!nctId)
            return false;
        // NCT ID format: NCT followed by 8 digits
        return /^NCT\d{8}$/.test(nctId);
    }
    static splitAndTrim(value, delimiter) {
        if (!value)
            return [];
        return value.split(delimiter)
            .map(item => item.trim())
            .filter(item => item.length > 0);
    }
    static createArrayOfObjects(values, objectKey) {
        if (!Array.isArray(values))
            return [];
        return values.map(value => ({ [objectKey]: value }));
    }
}
exports.SharePointTransformUtils = SharePointTransformUtils;
/**
 * Apply a single mapping rule to transform SharePoint data
 */
function applyMappingRule(rule, sourceData, targetData, errors, warnings) {
    try {
        let value;
        // Get source value or use fixed value
        if (rule.source === null) {
            value = rule.value;
        }
        else {
            value = sourceData[rule.source];
        }
        // Skip if no value and not a fixed value
        if (value === undefined || value === null) {
            if (rule.source !== null) {
                warnings.push(`Missing source field: ${rule.source}`);
            }
            return;
        }
        // Apply transformations
        if (rule.transform) {
            switch (rule.transform) {
                case "createSlug":
                    value = (0, index_1.createSlug)(String(value));
                    break;
                case "parseDate":
                    value = SharePointTransformUtils.parseDate(String(value));
                    break;
                default:
                    warnings.push(`Unknown transform: ${rule.transform}`);
            }
        }
        // Handle string splitting
        if (rule.split && typeof value === "string") {
            value = SharePointTransformUtils.splitAndTrim(value, rule.split);
        }
        // Handle array of objects transformation
        if (rule.asArrayOfObjects && Array.isArray(value)) {
            const objectKey = Object.keys(rule.asArrayOfObjects)[0];
            value = SharePointTransformUtils.createArrayOfObjects(value, objectKey);
        }
        // Set target value(s)
        const targets = Array.isArray(rule.target) ? rule.target : [rule.target];
        for (const target of targets) {
            const targetPath = target.split('.');
            let current = targetData;
            // Navigate to the target location
            for (let i = 0; i < targetPath.length - 1; i++) {
                const key = targetPath[i];
                if (!(key in current)) {
                    current[key] = {};
                }
                current = current[key];
            }
            const finalKey = targetPath[targetPath.length - 1];
            // Handle append mode
            if (rule.append && current[finalKey]) {
                if (Array.isArray(current[finalKey]) && Array.isArray(value)) {
                    current[finalKey] = [...current[finalKey], ...value];
                }
                else {
                    current[finalKey] = value;
                }
            }
            else {
                current[finalKey] = value;
            }
        }
    }
    catch (error) {
        errors.push(`Error applying mapping rule for ${rule.source || 'fixed value'}: ${error instanceof Error ? error.message : String(error)}`);
    }
}
/**
 * Create a base clinical trial from SharePoint data
 */
function createBaseTrialFromSharePoint(sharePointItem) {
    const errors = [];
    const warnings = [];
    // Initialize base trial structure
    const trial = {
        name: "",
        published: "published",
        data: {
            locale: "en",
            slug: "",
            title: "",
            source: {
                data: sharePointItem,
                system: "SHAREPOINT"
            }
        }
    };
    // Apply all mapping rules
    for (const rule of sharepoint_mapping_json_1.default) {
        applyMappingRule(rule, sharePointItem, trial, errors, warnings);
    }
    // Validate required fields
    if (!trial.data.title) {
        errors.push("Missing required field: title");
    }
    if (!trial.data.slug) {
        errors.push("Missing required field: slug");
    }
    if (!trial.name) {
        trial.name = `SHAREPOINT-${sharePointItem.Id}`;
    }
    // Validate NCT ID if present
    if (trial.data.identification?.nctId) {
        if (!SharePointTransformUtils.validateNctId(trial.data.identification.nctId)) {
            warnings.push(`Invalid NCT ID format: ${trial.data.identification.nctId}`);
        }
    }
    // Clean phone numbers
    if (trial.data.contactsLocations?.phoneNumbers) {
        trial.data.contactsLocations.phoneNumbers = trial.data.contactsLocations.phoneNumbers.map(phone => ({
            number: SharePointTransformUtils.cleanPhoneNumber(phone.number || "")
        }));
    }
    index_1.logger.debug("Transformed SharePoint item", "sharepoint-mapping", {
        sourceId: sharePointItem.Id,
        trialName: trial.name,
        errors: errors.length,
        warnings: warnings.length
    });
    return {
        original: sharePointItem,
        transformed: trial,
        enriched: false, // Will be set to true if ClinicalTrials.gov enrichment is applied
        errors,
        warnings
    };
}
/**
 * Transform multiple SharePoint items
 */
function transformSharePointItems(sharePointItems) {
    index_1.logger.info(`Starting transformation of ${sharePointItems.length} SharePoint items`, "sharepoint-mapping");
    const results = [];
    for (const item of sharePointItems) {
        try {
            const result = createBaseTrialFromSharePoint(item);
            results.push(result);
        }
        catch (error) {
            index_1.logger.error(`Error transforming SharePoint item ${item.Id}`, "sharepoint-mapping", error);
            results.push({
                original: item,
                transformed: {},
                enriched: false,
                errors: [`Failed to transform item: ${error instanceof Error ? error.message : String(error)}`],
                warnings: []
            });
        }
    }
    const successful = results.filter(r => r.errors.length === 0);
    const failed = results.filter(r => r.errors.length > 0);
    index_1.logger.info(`Transformation completed: ${successful.length} successful, ${failed.length} failed`, "sharepoint-mapping");
    return results;
}
/**
 * Validate that a transformed trial conforms to the ClinicalTrial interface
 */
function validateClinicalTrial(trial) {
    const errors = [];
    // Required fields
    if (!trial.name)
        errors.push("Missing required field: name");
    if (!trial.data)
        errors.push("Missing required field: data");
    if (!trial.data?.locale)
        errors.push("Missing required field: data.locale");
    if (!trial.data?.slug)
        errors.push("Missing required field: data.slug");
    if (!trial.data?.title)
        errors.push("Missing required field: data.title");
    // Type validations
    if (trial.published && !["published", "draft"].includes(trial.published)) {
        errors.push("Invalid published status: must be 'published' or 'draft'");
    }
    // Nested object validations
    if (trial.data?.identification?.nctId) {
        if (!SharePointTransformUtils.validateNctId(trial.data.identification.nctId)) {
            errors.push(`Invalid NCT ID format: ${trial.data.identification.nctId}`);
        }
    }
    // Array validations
    if (trial.data?.eligibility?.ageGroup && !Array.isArray(trial.data.eligibility.ageGroup)) {
        errors.push("ageGroup must be an array");
    }
    if (trial.data?.description?.conditions && !Array.isArray(trial.data.description.conditions)) {
        errors.push("conditions must be an array");
    }
    if (trial.data?.description?.interventions && !Array.isArray(trial.data.description.interventions)) {
        errors.push("interventions must be an array");
    }
    // Contact validations
    if (trial.data?.contactsLocations?.contacts) {
        if (!Array.isArray(trial.data.contactsLocations.contacts)) {
            errors.push("contacts must be an array");
        }
        else {
            trial.data.contactsLocations.contacts.forEach((contact, index) => {
                if (typeof contact !== "object" || !contact.name) {
                    errors.push(`Contact ${index} must have a name property`);
                }
            });
        }
    }
    if (trial.data?.contactsLocations?.phoneNumbers) {
        if (!Array.isArray(trial.data.contactsLocations.phoneNumbers)) {
            errors.push("phoneNumbers must be an array");
        }
        else {
            trial.data.contactsLocations.phoneNumbers.forEach((phone, index) => {
                if (typeof phone !== "object" || !phone.number) {
                    errors.push(`Phone number ${index} must have a number property`);
                }
            });
        }
    }
    return errors;
}
